import { Alert, Stack, TextField } from '@mui/material'
import { CanceledError, isAxiosError } from 'axios'
import React, { useCallback, useEffect, useState } from 'react'

import { LoadingButton } from 'components/common/LoadingButton'
import { TestEvaluationResultsStatusCheckIntegration } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { useValidateEndpointConfiguration } from 'components/status-checks/hooks/use-validate-endpoint-configuration'
import { useValidateEvaluationConfiguration } from 'components/status-checks/hooks/use-validate-evaluation-configuration'
import { type StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import toast from 'lib/toast'
import { type EvaluationResults } from 'types/api/status-checks/evaluation'
import {
  type EndpointConfiguration,
  type EndpointResponse,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { getMessageFromResponseOrError } from 'utils/strings'

const TestStatusCheck: React.FC<{
  disabled: boolean
  integration: TeamExternalIntegration
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  onSuccess(endpointResponse: EndpointResponse): void
}> = ({
  disabled,
  integration,
  endpointConfiguration,
  evaluationConfiguration,
  onSuccess
}) => {
  const {
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  /*
   * Request Cancellation elements/flags.
   * Once an AbortController is aborted, it cannot be reset, and needs to be recreated.
   * Notably, the `signal.aborted` flag triggers once the request is canceled, which may not be immediate.
   */
  const [abortController, setAbortController] = useState(new AbortController())
  const [isCanceling, setIsCanceling] = useState(false)
  const [isCanceled, setIsCanceled] = useState(false)

  // Response details state
  const [authResponseCode, setAuthResponseCode] = useState<number | null>(null)
  const [authResponseBody, setAuthResponseBody] = useState<string | null>(null)
  const [endpointResponse, setEndpointResponse] = useState<EndpointResponse>()
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResults>()

  const {
    isError: hasEndpointValidationError,
    isLoading: isEndpointValidating,
    isSuccess: isEndpointValidated,
    reset: resetValidation,
    validateEndpointConfiguration
  } = useValidateEndpointConfiguration(abortController)

  const {
    isError: hasEvaluationValidationError,
    isLoading: isEvaluationValidating,
    isSuccess: isEvaluationValidated,
    reset: resetEvaluationValidation,
    validateEvaluationConfiguration
  } = useValidateEvaluationConfiguration(abortController)

  // Derived validation state flags
  const isValidating = isEndpointValidating || isEvaluationValidating
  const isValidated = isEndpointValidated && isEvaluationValidated
  const hasError = hasEndpointValidationError || hasEvaluationValidationError

  // Flags for various action button states
  const isTestActionDisabled = disabled || isValidating
  const isCancelActionDisabled = !isValidating || isCanceling

  const resetValidations = useCallback(() => {
    resetValidation()
    resetEvaluationValidation()
  }, [resetEvaluationValidation, resetValidation])

  const resetAuthResponseDetails = useCallback(() => {
    setAuthResponseCode(null)
    setAuthResponseBody(null)
    setEndpointResponse(undefined)
    setEvaluationResults(undefined)
    setIsStatusCheckConnectionValidated(false)
    setIsStatusCheckEvaluationValidated(false)
  }, [setIsStatusCheckConnectionValidated, setIsStatusCheckEvaluationValidated])

  /*
   * Must go through the process of evaluating both:
   * 1. Endpoint Configuration Validation
   * 2. Evaluation Configuration Validation
   * As each step validates, context flags are updated to reflect the validation state.
   */
  const handleTestHealthCheck = async () => {
    resetAuthResponseDetails()
    resetValidations()

    try {
      // First, validate the endpoint configuration. The hook handles PNI vs public integration cases internally.
      const { testEndpointResponse } = await validateEndpointConfiguration(
        integration,
        endpointConfiguration
      )

      // Update local/context state to reflect successful endpoint validation
      setEndpointResponse(testEndpointResponse)
      setIsStatusCheckConnectionValidated(true)
      // NOTE: `onSuccess` here is meant to refer to the endpoint evaluation phase.
      onSuccess(testEndpointResponse)

      // Second, validate the evaluation configuration against the endpoint response. Again PNI vs public cases are handled internally.
      const evaluationResults = await validateEvaluationConfiguration({
        endpointConfiguration,
        endpointResponse: testEndpointResponse,
        evaluationConfiguration,
        integration
      })

      // Update local/context state to reflect successful evaluation validation
      setIsStatusCheckEvaluationValidated(true)
      setEvaluationResults(evaluationResults.testEvaluationResults)

      // Reset any lingering cancellation state flags if the request chain completes before the abort request is fulfilled.
      setIsCanceling(false)
      setIsCanceled(false)
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        setIsCanceling(false)
        setIsCanceled(true)
        return
      }

      // In the event this is not an axios error, at least throw an error toast notification to inform the user.
      if (!isAxiosError(error)) {
        toast({
          type: 'error',
          text: getMessageFromResponseOrError(
            error,
            'An unknown error occurred while testing the Health Check.'
          )
        })
        return
      }

      const { response } = error
      const { data = null, status } = response || {}

      setAuthResponseBody(data)
      setAuthResponseCode(status ?? null)
      setIsStatusCheckConnectionValidated(false)
      setIsStatusCheckEvaluationValidated(false)
    }
  }

  // Re-creates the abort controller if the previous one was aborted.
  useEffect(() => {
    if (!abortController.signal.aborted) return

    setAbortController(new AbortController())
  }, [abortController.signal.aborted])

  return (
    <Stack data-test-status-check spacing={2}>
      <Stack direction="row" spacing={2}>
        <LoadingButton
          data-testid="test-status-check"
          isDisabled={isTestActionDisabled}
          isLoading={isValidating}
          size="small"
          onClick={handleTestHealthCheck}
        >
          {isValidating ? 'Testing Health Check...' : 'Test Health Check'}
        </LoadingButton>
        <LoadingButton
          color="secondary"
          data-testid="cancel-test-status-check"
          isDisabled={isCancelActionDisabled}
          isLoading={isCanceling}
          size="small"
          variant="contained"
          onClick={() => {
            setIsCanceling(true)
            abortController.abort()
          }}
        >
          {isCanceling ? 'Canceling Test...' : 'Cancel Test Health Check'}
        </LoadingButton>
      </Stack>
      {hasError && !isCanceled && (
        <Alert data-testid="test-status-check-alert" severity="error">
          Monitor URL returned an error or was unreachable. See response details
          below.
        </Alert>
      )}
      {Boolean(authResponseCode) && (
        <TextField
          data-testid="test-status-check-integration-response-code"
          disabled={true}
          fullWidth={true}
          label="Response Code"
          value={`${authResponseCode}`}
        />
      )}
      {Boolean(authResponseBody) && (
        <TextField
          multiline
          data-testid="test-status-check-integration-response-body"
          disabled={true}
          fullWidth={true}
          label="Response Body"
          rows={4}
          value={authResponseBody}
        />
      )}
      {isValidated && evaluationResults && endpointResponse && (
        <TestEvaluationResultsStatusCheckIntegration
          endpointResponse={endpointResponse}
          evaluationResults={evaluationResults}
          integration={integration}
        />
      )}
    </Stack>
  )
}

export default TestStatusCheck
